import axios from 'axios';
import React, { Dispatch, SetStateAction } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ConfigureRequest, GetSystemResponse, Part, SaveManifoldAndValvesSizingProgressRequest, SizeManifoldAndValvesRequest, SizingNotification } from '../models/Api';
import { ITabSystemData } from '../pages/DynamicTabSystem/DynamicTabSystem';
import ApiService from './ApiService';
import { launchSizingTool } from '../utils/Utils';
import SystemService from './SystemService';
import PdnManifoldSizingTool from '../components/SizingTools/PdnManifoldSizingTool';
import UnitService from './UnitService';
import systemGroups from './GroupService';
import { GroupUnion } from '../models/GroupUnion';
import LegacySizingTool from '../components/SizingTools/LegacySizingTool';

export const dashboardTabWorkflowId = 'dashboard';
export const configureTabWorkflowId = 'configure';

export const tabClosingEventType = 'tab-closing';
export const compareCleanupEventType = 'compare-cleanup';

export type SizingToolState = {
  part: Part;
  showMiniSizing: boolean;
  createPartSizingData: boolean;
  sizingUnderway: boolean;
};

type SizingToolLauncher = (system: GetSystemResponse, part: Part) => JSX.Element;

const initialState = {
  part: {} as Part,
  showMiniSizing: false,
  createPartSizingData: false,
  sizingUnderway: false,
};

const state$ = new BehaviorSubject<SizingToolState>(initialState);

const setState = (data: SizingToolState) => {
  return state$.next(data);
};

export type TabState = {
  workflowId: string;
  isOpen: boolean;
};

type InitialTabStateType = { tabState: TabState; tabSystemDataState: ITabSystemData };

const initialTabState = {
  tabState: { workflowId: '', isOpen: false },
  tabSystemDataState: {
    details: [],
    activeTabWorkflowId: dashboardTabWorkflowId,
  },
};

const tabState$ = new BehaviorSubject<InitialTabStateType>(initialTabState);

const setTabState = (data: InitialTabStateType) => {
  return tabState$.next(data);
};

const pdnValvePartCategoryName = 'PneumaticDirectionalControlValves';
const valveMountedOnManifoldPartCategoryName = `${pdnValvePartCategoryName}MountedOnManifold`;

const getManifoldStructure = (system: GetSystemResponse, part: Part) => {
  const unitDefinitions = UnitService.getCurrentState();
  const groups = systemGroups(system, unitDefinitions);
  const partCategory = system.partCategories?.find((category) => category.id === part.categoryId);
  if (partCategory?.name === pdnValvePartCategoryName) {
    const segmentGroup = groups.find((group) => group.instance.id === part.groupInstanceId) as GroupUnion;
    const manifoldGroup = groups.find((group) => group.instance.id === segmentGroup.instance.parentGroupId) as GroupUnion;
    const manifoldPart = manifoldGroup.parts[0].instance;
    const workflowId = manifoldPart.workflowId as string;
    return {
      segmentGroup: segmentGroup,
      manifoldGroup: manifoldGroup,
      manifoldPart: manifoldPart,
      workflowId: workflowId,
    };
  }
  return {
    segmentGroup: groups.find((group) => group.instance.parentGroupId === part.groupInstanceId) as GroupUnion,
    manifoldGroup: groups.find((group) => group.instance.id === part.groupInstanceId) as GroupUnion,
    manifoldPart: part,
    workflowId: part.workflowId as string,
  };
};

const getPdnManifoldAndValveSizingTool = (system: GetSystemResponse, part: Part): JSX.Element => {
  const manifold = getManifoldStructure(system, part);
  return (
    <PdnManifoldSizingTool
      part={manifold.manifoldPart}
      segmentGroup={manifold.segmentGroup}
      manifoldGroup={manifold.manifoldGroup}
      system={system as GetSystemResponse}
    />
  );
};

const buildLegacySizingTool = (part: Part) => {
  const url = `${process.env.REACT_APP_WORKFLOW_ROUTER_URL}${part.workflowId}`;
  return <LegacySizingTool iframeUrl={url} part={part} />;
};

const partCategoryNameToSizingToolLauncherMap = new Map<string, SizingToolLauncher>();
partCategoryNameToSizingToolLauncherMap.set('PneumaticManifoldMasterPart', getPdnManifoldAndValveSizingTool);
partCategoryNameToSizingToolLauncherMap.set(valveMountedOnManifoldPartCategoryName, getPdnManifoldAndValveSizingTool);

function parseSizingNotification(e: MessageEvent) {
  const data = JSON.parse(e.data.data);
  const sizingNotification = {
    systemId: data.systemId,
    workflowId: data.workflowId,
    closeTabs: true,
  } as SizingNotification;
  return [data.partId, sizingNotification];
}

function handleWindowMessageEvent(e: MessageEvent) {
  if (e.data.type === 'frameHeight') {
    const newHeight = e.data.data;
    const frame = document.getElementById('sizingTabFrame') as HTMLIFrameElement;
    frame.style.height = newHeight + 'px';
  }
  
  if (e.data.type === 'sizingNotification') {
    const [partId, sizingNotification] = parseSizingNotification(e);
    const api = ApiService.getApi();
    api.partsSizingNotificationCreate(partId, sizingNotification);
  }

  if (e.data.type === 'returnToSizingNotification') {
    const [partId, sizingNotification] = parseSizingNotification(e);
    const api = ApiService.getApi();
    api.partsReturnToSizingNotificationCreate(partId, sizingNotification);
  }

  if (e.data.type === 'scrollToTabStrip') {
    document.getElementById('dynaTabStrip')?.scrollIntoView();
  }
}

const getPartCategoryNameForSizing = (system: GetSystemResponse, part: Part): string => {
  const partCategory = system.partCategories?.find((category) => category.id === part.categoryId);
  if (partCategory?.name !== pdnValvePartCategoryName) {
    return partCategory?.name as string;
  }
  const partGroup = system.groups?.find((group) => group.id === part.groupInstanceId);
  return partGroup?.parentGroupId ? valveMountedOnManifoldPartCategoryName : (partCategory.name as string);
};

const SizingToolService = {
  setSizingToolState: (part: Part, showMiniSizing: boolean, createPartSizingData: boolean, sizingUnderway: boolean) => {
    setState({
      part: part,
      showMiniSizing: showMiniSizing,
      createPartSizingData: createPartSizingData,
      sizingUnderway: sizingUnderway,
    });
  },

  getCurrentState: () => {
    return state$.getValue();
  },

  getState: (setSizingToolState: Dispatch<SetStateAction<SizingToolState>>) => {
    return state$.asObservable().subscribe((next) => setSizingToolState(next));
  },

  setSizingUnderway: (sizingUnderway: boolean) => {
    const modified = SizingToolService.getCurrentState();
    modified.sizingUnderway = sizingUnderway;
    setState(modified);
  },

  assignTabState: (workflowId: string, isOpen: boolean) => {
    setTabState({
      tabState: { workflowId: workflowId, isOpen: isOpen },
      tabSystemDataState: tabState$.value.tabSystemDataState,
    });
  },

  assignTabSystemDataState: (modifiedTabSystemData: ITabSystemData) => {
    setTabState({
      tabState: tabState$.value.tabState,
      tabSystemDataState: modifiedTabSystemData,
    });
  },

  getTabState: (tabStateCallback: Dispatch<SetStateAction<TabState>>) => {
    return tabState$.asObservable().subscribe((next) => tabStateCallback(next.tabState));
  },

  getTabSystemDataState: (tabStateCallback: Dispatch<SetStateAction<ITabSystemData>>) => {
    return tabState$.asObservable().subscribe((next) => tabStateCallback(next.tabSystemDataState));
  },

  getTabOpenState: () => {
    return tabState$.value.tabState.isOpen;
  },

  getTabSystemDataPrevState: () => {
    return tabState$.value.tabSystemDataState;
  },

  closeTab: (workflowId: string, focusDashboard = true) => {
    if (SizingToolService.getTabOpenState()) SizingToolService.assignTabState(dashboardTabWorkflowId, false);

    if (!workflowId || workflowId === dashboardTabWorkflowId) {
      return; //do nothing... never remove the dashboard tab
    }

    SizingToolService.setSizingUnderway(false);

    const previousState = SizingToolService.getTabSystemDataPrevState();
    const newState: ITabSystemData = {
      details: [],
      activeTabWorkflowId: previousState.activeTabWorkflowId,
    };

    previousState.details.forEach((tabDetail, i) => {
      if (tabDetail.workflowId === workflowId) {
        const closeEvent = new CustomEvent(tabClosingEventType, {
          cancelable: true,
          detail: { workflowId: workflowId },
        });
        window.dispatchEvent(closeEvent);
        if (previousState.activeTabWorkflowId === workflowId) {
          newState.activeTabWorkflowId = focusDashboard ? dashboardTabWorkflowId : previousState.details[i - 1].workflowId;
        }
      } else {
        const tabDetailCopy = { ...tabDetail };
        newState.details.push(tabDetailCopy);
      }
    });

    SizingToolService.assignTabSystemDataState(newState);
  },

  closeConfigureTab: () => {
    SizingToolService.closeTab(configureTabWorkflowId, false);
  },

  returnToSizing: (partId: string) => {
    const system = SystemService.getCurrentState().system;
    const partBeingSized = system?.parts?.find((part) => part.id === partId);
    launchSizingTool(system, partBeingSized as Part);
  },

  isPartBeingSized: (workflowId: string) => {
    return tabState$.value.tabSystemDataState.details.some((tabDetail) => tabDetail.workflowId === workflowId);
  },

  startMessageHandler: () => {
    window.addEventListener('message', handleWindowMessageEvent);
  },

  stopMessageHandler: () => {
    window.removeEventListener('message', handleWindowMessageEvent);
  },

  getSizingTool: (system: GetSystemResponse, part: Part): JSX.Element => {
    const partCategoryName = getPartCategoryNameForSizing(system, part);
    let tool: JSX.Element;
    const getTool = partCategoryNameToSizingToolLauncherMap.get(partCategoryName);
    if (getTool === undefined) {
      tool = buildLegacySizingTool(part);
    } else {
      tool = getTool(system, part);
    }
    return tool;
  },

  getSizingToolTitle: (system: GetSystemResponse, part: Part): string => {
    const partCategory = getPartCategoryNameForSizing(system, part);
    if (partCategory === valveMountedOnManifoldPartCategoryName) {
      return getManifoldStructure(system, part).manifoldPart.name as string;
    }
    return part.name as string;
  },

  getLegacySizingTool: (part: Part): JSX.Element => {
    return buildLegacySizingTool(part);
  },

  getSizingManifoldAndValvesProgressDetail: async (workflowId: string) => {
    const api = ApiService.getApi();
    const response = await api.sizingManifoldAndValvesProgressDetail(workflowId);
    return response.data;
  },

  createSizingManifoldAndValves: async (data: SizeManifoldAndValvesRequest) => {
    const api = ApiService.getApi();
    const response = await api.sizingManifoldAndValvesCreate(data);
    if (response.status === 200) {
      return response.data;
    }
  },

  createSizingManifoldAndValvesProgress: async (workflowId: string, data: SaveManifoldAndValvesSizingProgressRequest) => {
    const api = ApiService.getApi();
    const response = await api.sizingManifoldAndValvesProgressCreate(workflowId, data);
    if (response.status === 200) {
      return response.data;
    }
  },

  getSizingProductCacheStatusList: async () => {
    const api = ApiService.getApi();
    const response = await api.sizingProductCacheStatusList();
    if (response.status === 200) {
      return response.data;
    }
  },

  getSizingFilterSessionDetail: async (workflowId: string) => {
    const api = ApiService.getApi();
    const response = await api.sizingFilterSessionDetail(workflowId);
    if (response.status === 200) {
      return response.data;
    }
  },

  getSizingGenericResultsDetail: async (workflowId: string) => {
    const api = ApiService.getApi();
    const response = await api.sizingGenericResultsDetail(workflowId);
    if (response.status === 200) {
      return response.data;
    }
  },

  compareApiHelper: async (part: Part, partCategoryName: string, isMetric: boolean) => {
    const response = await axios
      .get(
        `${process.env.REACT_APP_LEGACY_BASE_HOST}/Sizing/CompareApiHelper?workflowId=${
          part.workflowId
        }&locale=en-US&partCategoryName=${partCategoryName}&isImperial=${!isMetric}`,
      )
      .then((res) => {
        return res;
      })
      .catch((error) => {
        return error;
      });
    return response;
  },
  resetWorkFlow: async (workFlowId: string) => {
    const response = await axios.get(
      `${process.env.REACT_APP_LEGACY_BASE_HOST}/System/RedirectToWFClearComplete?state=Complete&WFID=${workFlowId}&noRedirect=true&stepName=Add`,
    );
    return response.status === 200;
  },
  createSizingConfigure: async (data: ConfigureRequest) => {
    const api = ApiService.getApi();
    const response = await api.sizingConfigureCreate(data);
    if (response.status === 200) {
      return response;
    }
  },

  updateCalculatedMetadataForParts: async (partIds: string[]) => {
    const api = ApiService.getApi();
    const response = await api.partsBatchUpdateCalculatedMetadataUpdate({ partIds: partIds });
    const system = SystemService.getCurrentState().system;
    if (system) {
      const systemCopy = { ...system };
      partIds.map((partId) => {
        const partToUpdate = systemCopy?.parts?.find((part) => part.id === partId) as Part;
        const partIdIndex = response?.data.results.findIndex((part) => part.partId == partId);
        if (response?.data.results[partIdIndex].calculationResult.success) {
          partToUpdate.metadataInstances = response?.data.results[partIdIndex].metadataInstances;
        }
      });
      SystemService.updateSystemInState(systemCopy);
    }
    return response?.data.results.map((metaData) => metaData.calculationResult.success as boolean);
  },
};

export default SizingToolService;
